import { Route } from '@angular/router';
import {
  FEATURE_FLAG_KEY,
  FEATURE_FLAGS,
  pathAllowedByFeatureFlagGuard,
  REQUEST_CONTEXT_ADAPTER,
  RequestDiscussionService,
  RequestService,
  UPLOAD_FILES_URL_KEY,
  VENDOR_ROUTES,
  VendorRequestContextAdapter,
  VendorRequestDiscussionService,
  VendorRequestService,
  vendorUploadFileUrls,
  VendorWorkflowAccessKeyService,
  WorkflowAccessKeyService,
} from '@kuiper/ibusiness-core';

const vendorPortalProviders = [
  {
    provide: REQUEST_CONTEXT_ADAPTER,
    useExisting: VendorRequestContextAdapter,
  },
  {
    provide: RequestService,
    useExisting: VendorRequestService,
  },
  {
    provide: WorkflowAccessKeyService,
    useExisting: VendorWorkflowAccessKeyService,
  },
  {
    provide: RequestDiscussionService,
    useExisting: VendorRequestDiscussionService,
  },
];

export const vendorRoutes: Route[] = [
  {
    path: '',
    providers: vendorPortalProviders,
    data: {
      [UPLOAD_FILES_URL_KEY]: vendorUploadFileUrls,
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: VENDOR_ROUTES.VENDOR_DATA,
      },
      {
        path: VENDOR_ROUTES.ACCOUNT_SETTINGS.MAIN,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('../pages/vendor-flow/vendor-account-setting/vendor-account-setting.component').then(
                m => m.VendorAccountSettingComponent
              ),
          },
          {
            path: VENDOR_ROUTES.ACCOUNT_SETTINGS.EDIT_PROFILE.replace('/', ''),
            loadComponent: () =>
              import('../pages/vendor-flow/vendor-account-setting/edit-vendor-profile/edit-vendor-profile.component').then(
                m => m.EditVendorProfileComponent
              ),
          },
        ],
      },
      {
        path: VENDOR_ROUTES.VENDOR_DATA,
        loadComponent: () =>
          import('../pages/vendor-flow/vendor-data/vendor-data.component').then(
            m => m.VendorDataComponent
          ),
      },
      {
        path: VENDOR_ROUTES.INVOICES.MAIN,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: VENDOR_ROUTES.INVOICES.LIST,
          },
          {
            path: VENDOR_ROUTES.INVOICES.LIST,
            loadComponent: () =>
              import('../pages/vendor-flow/invoice/vendor-invoice-list.component').then(
                m => m.VendorInvoiceListComponent
              ),
          },
          {
            path: `${VENDOR_ROUTES.INVOICES.DETAILS}/:id`,
            loadComponent: () =>
              import('../pages/vendor-flow/invoice/vendor-invoice-details/vendor-invoice-details.component').then(
                m => m.VendorInvoiceDetailsComponent
              ),
          },
        ],
      },
      {
        path: VENDOR_ROUTES.CONTRACTS.MAIN,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: VENDOR_ROUTES.CONTRACTS.LIST,
          },
          {
            path: VENDOR_ROUTES.CONTRACTS.LIST,
            loadComponent: () =>
              import('../pages/vendor-flow/contracts/contract-list/vendor-contract-list.component').then(
                m => m.VendorContractListComponent
              ),
          },
          {
            path: `${VENDOR_ROUTES.CONTRACTS.DETAILS}/:contractId`,
            loadComponent: () =>
              import('../pages/vendor-flow/contracts/vendor-contract-details/vendor-contract-details.component').then(
                m => m.VendorContractDetailsComponent
              ),
          },
        ],
      },
      {
        path: VENDOR_ROUTES.MY_INBOX.ASSIGNED_TASKS,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('../pages/vendor-flow/my-tasks/my-tasks.component').then(
                m => m.MyTasksComponent
              ),
          },
          {
            path: 'request/:requestAction/:flowId',
            loadComponent: () =>
              import('@kuiper/ibusiness-core').then(
                m => m.RequestViewComponent
              ),
          },
          {
            path: ':stepId/:requestAction/:flowId',
            loadComponent: () =>
              import('@kuiper/ibusiness-core').then(m => m.RequestComponent),
          },
        ],
      },
      {
        path: '',
        canActivateChild: [pathAllowedByFeatureFlagGuard],
        data: {
          [FEATURE_FLAG_KEY]: FEATURE_FLAGS.DOMO.invoice_request,
        },
        children: [
          {
            path: VENDOR_ROUTES.REQUESTS.LIST,
            async loadComponent() {
              const c = await import('@kuiper/ibusiness-core');
              return c.RequestListComponent;
            },
          },
          {
            path: `${VENDOR_ROUTES.REQUESTS.DETAILS}/:id`,
            async loadComponent() {
              const c = await import('@kuiper/ibusiness-core');
              return c.RequestDetailsComponent;
            },
          },
          {
            path: `${VENDOR_ROUTES.REQUESTS.DETAILS}/:requestId/${VENDOR_ROUTES.REQUESTS.SCHEDULE}`,
            async loadComponent() {
              const c = await import('@kuiper/ibusiness-core');
              return c.ScheduleWorkflowComponent;
            },
          },
          {
            path: `${VENDOR_ROUTES.REQUESTS.DETAILS}/:requestId/${VENDOR_ROUTES.REQUESTS.SCHEDULE}/${VENDOR_ROUTES.REQUESTS.ACTION.VIEW}/:scheduleId`,
            async loadComponent() {
              const c = await import('@kuiper/ibusiness-core');
              return c.ScheduleDetailsComponent;
            },
          },
          {
            path: `${VENDOR_ROUTES.REQUESTS.DETAILS}/:requestId/${VENDOR_ROUTES.REQUESTS.SCHEDULE}/:scheduleId`,
            async loadComponent() {
              const c = await import('@kuiper/ibusiness-core');
              return c.ScheduleWorkflowComponent;
            },
          },
          {
            path: `${VENDOR_ROUTES.REQUESTS.DETAILS}/:requestId/:requestAction`,
            async loadComponent() {
              const c = await import('@kuiper/ibusiness-core');
              return c.RequestComponent;
            },
          },
          {
            path: `${VENDOR_ROUTES.REQUESTS.REQUEST}/:requestAction/:flowId`,
            async loadComponent() {
              const c = await import('@kuiper/ibusiness-core');
              return c.RequestViewComponent;
            },
          },
          {
            path: `${VENDOR_ROUTES.REQUESTS.SEQUENTIAL_REQUEST}/:requestAction/:flowId`,
            async loadComponent() {
              const c = await import('@kuiper/ibusiness-core');
              return c.SequentialRequestComponent;
            },
          },
          {
            path: `${VENDOR_ROUTES.REQUESTS.DETAILS}/:requestId/:requestAction/:projectId`,
            async loadComponent() {
              const c = await import('@kuiper/ibusiness-core');
              return c.RequestComponent;
            },
          },
        ],
      },
    ],
  },
];
